// apps/lk/src/components/dashboard/SynthesisBlock.tsx
// Синтез-анализ: статья-стиль с глубокими описаниями.

'use client';
import { useState } from 'react';
import {
  Target, Heart, ShieldCheck, Ruler, Lightbulb, Handshake, Star,
  Sparkles, Zap, AlertTriangle, CheckCircle2, ChevronDown,
  Award, Crown, Compass, Shield, Flame, HeartHandshake, Globe, Scale, Gem,
  type LucideIcon,
} from 'lucide-react';
import {
  COMPOSITE_CONTENT, coherenceText, overallScoreText, SYNERGY_RICH, RISK_RICH,
  AXIS_CONTENT, MOTIVATOR_CONTENT,
  generatePortrait,
} from './synthesis-content';

const ICON_MAP = {
  Award, Crown, Compass, Shield, Flame, HeartHandshake, Globe, Scale, Gem, Star,
  Target, Heart, ShieldCheck, Ruler, Lightbulb, Handshake, Sparkles, Zap,
} as const;

interface SynthesisData {
  overallScore: number;
  coherenceScore: number;
  cLeadership: number; cLeadershipTier: string;
  cEmpathy: number; cEmpathyTier: string;
  cResilience: number; cResilienceTier: string;
  cDiscipline: number; cDisciplineTier: string;
  cCreativity: number; cCreativityTier: string;
  cSocial: number; cSocialTier: string;
  cReliability: number; cReliabilityTier: string;
  axisEngine: string; axisHeart: string; axisFreedom: string; axisIndivid: string;
  synergies: { id: string; name: string }[];
  risks: { id: string; name: string; severity: string }[];
  motivators: { label: string; score: number; value: string; tension: boolean; amplifier: number; supported: boolean }[];
}

const COMPOSITES: { key: string; label: string; icon: LucideIcon }[] = [
  { key: 'Leadership', label: 'Лидерство', icon: Target },
  { key: 'Empathy', label: 'Эмпатия', icon: Heart },
  { key: 'Resilience', label: 'Стрессоустойчивость', icon: ShieldCheck },
  { key: 'Discipline', label: 'Дисциплина', icon: Ruler },
  { key: 'Creativity', label: 'Креативность', icon: Lightbulb },
  { key: 'Social', label: 'Социальность', icon: Handshake },
  { key: 'Reliability', label: 'Надёжность', icon: Star },
];

const TIER_INFO: Record<string, { word: string; color: string; bg: string; chip: string; hex: string }> = {
  strong: { word: 'Сильное', color: 'text-emerald-600', bg: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-700', hex: '#10b981' },
  medium: { word: 'Развитое', color: 'text-amber-600', bg: 'bg-amber-500', chip: 'bg-amber-100 text-amber-700', hex: '#f59e0b' },
  weak: { word: 'Зона роста', color: 'text-slate-500', bg: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600', hex: '#94a3b8' },
};

function tierKey(tier: string): 'strong' | 'medium' | 'weak' {
  return tier === 'strong' ? 'strong' : tier === 'medium' ? 'medium' : 'weak';
}

function IconBadge({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return <Icon className={className ?? 'h-5 w-5'} strokeWidth={1.8} />;
}

export function SynthesisBlock({ data }: { data: SynthesisData }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tabs, setTabs] = useState<Record<string, 'essence' | 'apply'>>({});

  const coh = coherenceText(data.coherenceScore);
  const portrait = generatePortrait(data);
  const overall = overallScoreText(data.overallScore);
  const overallTier = tierKey(
    data.overallScore >= 63 ? 'strong' : data.overallScore >= 38 ? 'medium' : 'weak'
  );
  const overallInfo = TIER_INFO[overallTier];
  const cohHex = data.coherenceScore >= 75 ? '#10b981' : data.coherenceScore >= 50 ? '#f59e0b' : '#94a3b8';

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
      {/* ════ HERO ════ */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 p-8 text-white">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-indigo-200">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
          Синтез-аналитика
        </div>
        <h2 className="mt-3 font-heading text-xl font-bold leading-snug sm:text-2xl">
          {portrait.split('.')[0]}.
        </h2>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-indigo-100">
          {portrait.split('.').slice(1).join('.').trim()}
        </p>

        <div className="mt-6 flex flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <ScoreCircle score={data.overallScore} color={overallInfo.hex} />
            <div>
              <p className="text-xs uppercase tracking-wider text-indigo-200">Общий балл</p>
              <p className="text-sm font-semibold">{overallInfo.word}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ScoreCircle score={data.coherenceScore} color={cohHex} />
            <div>
              <p className="text-xs uppercase tracking-wider text-indigo-200">Цельность</p>
              <p className="text-sm font-semibold">{coh.label}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ════ 01 ПОРТРЕТ ════ */}
      <div className="border-b border-slate-100 p-8">
        <SectionTitle num="01" title="Ваш портрет" />

        {/* Общий балл */}
        <div className="mt-4 rounded-2xl bg-slate-50 p-5">
          <div className="flex items-center gap-3">
            <ScoreCircle score={data.overallScore} color={overallInfo.hex} />
            <div>
              <p className="text-sm font-semibold text-slate-900">Общий балл — {data.overallScore}/100 · {overall.label}</p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{overall.text}</p>
          <p className="mt-2 flex items-start gap-2 text-sm font-medium text-slate-600">
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" strokeWidth={2} />
            {overall.tip}
          </p>
        </div>

        {/* Цельность */}
        <div className="mt-3 rounded-2xl bg-indigo-50/60 p-5">
          <div className="flex items-center gap-3">
            <ScoreCircle score={data.coherenceScore} color={cohHex} />
            <div>
              <p className="text-sm font-semibold text-indigo-700">Цельность — {data.coherenceScore}/100 · {coh.label}</p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{coh.text}</p>
          <p className="mt-2 flex items-start gap-2 text-sm font-medium text-indigo-600">
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
            {coh.tip}
          </p>
        </div>
      </div>

      {/* ════ 02 ВАШИ КАЧЕСТВА ════ */}
      <div className="border-b border-slate-100 p-6 sm:p-8">
        <SectionTitle num="02" title="Ваши качества" />
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          7 качеств, от сильных к слабым. Топ-3 — ваш капитал, нижние — пространство для роста.
        </p>
        <div className="mt-5 space-y-1.5">
          {COMPOSITES
            .map(c => ({
              ...c,
              score: data[`c${c.key}` as keyof SynthesisData] as number,
              tier: data[`c${c.key}Tier` as keyof SynthesisData] as string,
            }))
            .sort((a, b) => b.score - a.score)
            .map((c, idx) => {
              const tk = tierKey(c.tier);
              const t = TIER_INFO[tk];
              const content = COMPOSITE_CONTENT[c.key];
              const isOpen = expanded === c.key;
              const tab = tabs[c.key] ?? 'essence';
              const IconComp = c.icon;
              const isTop = idx < 3;
              const isGrowth = idx >= 5;
              const accent = isTop ? 'border-l-indigo-400' : isGrowth ? 'border-l-amber-400' : 'border-l-transparent';

              return (
                <div key={c.key} className={`rounded-xl border border-l-2 border-slate-200 bg-white ${accent}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : c.key)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-slate-50"
                  >
                    <IconBadge icon={IconComp} className={`h-5 w-5 shrink-0 ${isTop ? 'text-indigo-500' : isGrowth ? 'text-amber-500' : 'text-slate-400'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          {c.label}
                          {isTop && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-500">СИЛА</span>}
                          {isGrowth && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">РОСТ</span>}
                        </span>
                        <span className="shrink-0 text-xs font-medium text-slate-500">{c.score}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div className={`h-full rounded-full ${t.bg} transition-all duration-500`} style={{ width: `${c.score}%` }} />
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
                  </button>

                  {isOpen && (
                    <div className="mx-3 mb-3 rounded-xl bg-slate-50 p-4">
                      <p className="text-sm leading-relaxed text-slate-500">{content.essence}</p>

                      <div className="mt-3 flex gap-2 border-b border-slate-200 pb-2">
                        <button
                          onClick={() => setTabs(prev => ({ ...prev, [c.key]: 'essence' }))}
                          className={`rounded-lg px-3 py-1 text-xs font-medium transition ${tab === 'essence' ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          Суть
                        </button>
                        <button
                          onClick={() => setTabs(prev => ({ ...prev, [c.key]: 'apply' }))}
                          className={`rounded-lg px-3 py-1 text-xs font-medium transition ${tab === 'apply' ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          Применение
                        </button>
                      </div>

                      {tab === 'essence' ? (
                        <div className="mt-3 space-y-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Как это проявляется</p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-700">{content[tk].shows}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Как вас видят другие</p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-700">{content[tk].perception}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 space-y-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Где применять</p>
                            <p className="mt-1 text-sm leading-relaxed text-slate-700">{content[tk].apply}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Совет</p>
                            <p className="mt-1 text-sm leading-relaxed text-indigo-600">{content[tk].tip}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* ════ 03 ВНУТРЕННИЕ ОСИ ════ */}
      <div className="border-b border-slate-100 p-8">
        <SectionTitle num="03" title="Внутренние оси" />
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          4 оси, которые описывают, как вы устроены внутри. Это не оценки — это полюса, между которыми вы находитесь.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {(['Engine', 'Heart', 'Freedom', 'Individ'] as const).map(axisKey => {
            const val = Number(data[`axis${axisKey}` as keyof SynthesisData]);
            const pct = ((val + 100) / 2);
            const info = AXIS_CONTENT[axisKey];
            const lean = Math.abs(val) > 25 ? 'strong' : Math.abs(val) < 15 ? 'balanced' : 'moderate';
            const meaning = lean === 'balanced' ? info.balancedMeaning : val >= 0 ? info.posMeaning : info.negMeaning;
            const apply = lean === 'balanced' ? info.balancedApply : val >= 0 ? info.posApply : info.negApply;

            return (
              <div key={axisKey} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="mb-3 text-sm font-semibold text-slate-900">{info.label}</p>
                <div className="mb-3 flex items-center justify-between text-xs text-slate-400">
                  <span>{info.negLabel}</span>
                  <span>{info.posLabel}</span>
                </div>
                <div className="relative h-2 rounded-full bg-slate-200">
                  <div className="absolute left-1/2 h-full w-0.5 bg-slate-300" />
                  <div
                    className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white shadow-md transition-all"
                    style={{ left: `calc(${pct}% - 8px)`, background: val >= 0 ? '#6366f1' : '#f59e0b' }}
                  />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{meaning}</p>
                <p className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm leading-relaxed text-slate-500">
                  <span className="font-semibold text-slate-600">Где применять: </span>{apply}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ════ 04 СИНЕРГИИ И РИСКИ ════ */}
      {(data.synergies.length > 0 || data.risks.length > 0) && (
        <div className="border-b border-slate-100 p-8">
          <SectionTitle num="04" title="Синергии и риски" />
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Это уникальные паттерны — комбинации качеств, которые создают либо ваши сильные стороны, либо скрытые риски. Они возникают только при пересечении нескольких тестов.
          </p>
          <div className={`mt-5 grid gap-4 ${data.synergies.length > 0 ? 'lg:grid-cols-2' : ''}`}>
            {data.synergies.length > 0 && (
              <div className="space-y-3">
                {data.synergies.map(s => {
                  const rich = SYNERGY_RICH[s.id];
                  return (
                    <div key={s.id} className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
                      <div className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-emerald-600" strokeWidth={2} />
                        <p className="text-sm font-bold text-emerald-900">{rich?.name || s.name}</p>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-emerald-800">{rich?.desc || 'Уникальное сочетание ваших качеств.'}</p>
                      <p className="mt-3 rounded-lg bg-white/60 p-2.5 text-sm leading-relaxed text-emerald-700">
                        <span className="font-semibold">Где применять: </span>{rich?.apply}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            {data.risks.length > 0 && (
              <div className="space-y-3">
                {data.risks.map(r => {
                  const rich = RISK_RICH[r.id];
                  return (
                    <div key={r.id} className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" strokeWidth={2} />
                          <p className="text-sm font-bold text-amber-900">{rich?.name || r.name}</p>
                        </div>
                        {r.severity === 'high' && (
                          <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">высокий</span>
                        )}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-amber-800">{rich?.desc || 'Обратите внимание на эту динамику.'}</p>
                      <p className="mt-3 rounded-lg bg-white/60 p-2.5 text-sm leading-relaxed text-amber-700">
                        <span className="font-semibold">Что делать: </span>{rich?.action}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {data.synergies.length === 0 && data.risks.length > 0 && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4 text-center">
              <p className="text-sm text-slate-500">
                У вас пока не выявлено синергий — паттернов, где несколько качеств усиливают друг друга. Это нормально: синергии возникают при высоких показателях (63+) по нескольким качествам одновременно.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ════ 05 МОТИВАЦИЯ ════ */}
      {data.motivators && data.motivators.length > 0 && (
        <div className="border-b border-slate-100 p-8">
          <SectionTitle num="05" title="Что вас драйвит" />
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Ваши ведущие ценности — то, что придаёт смысл вашим действиям. Каждая ценность усиливается или ослабляется чертами вашей личности, создавая вашу уникальную мотивационную архитектуру.
          </p>
          <div className="mt-5 space-y-4">
            {data.motivators.map((m, i) => {
              const mc = MOTIVATOR_CONTENT[m.value];
              const MotivIcon = mc ? ICON_MAP[mc.icon as keyof typeof ICON_MAP] ?? Star : Star;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                  {/* Заголовок */}
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100">
                      <MotivIcon className="h-5 w-5 text-indigo-600" strokeWidth={2} />
                    </span>
                    <span className="min-w-0 flex-1 break-words text-sm font-bold text-slate-900">{m.label}</span>
                    <span className="shrink-0 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-bold text-indigo-600">{m.score}/100</span>
                  </div>
                  {mc && <p className="mt-2 text-xs leading-relaxed text-slate-400">{mc.essence}</p>}

                  {/* Суть ценности */}
                  {mc && (
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{mc.meaning}</p>
                  )}

                  {/* Где применять */}
                  {mc && (
                    <p className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm leading-relaxed text-slate-500">
                      <span className="font-semibold text-slate-600">Где применять: </span>{mc.apply}
                    </p>
                  )}

                  {/* Состояние: напряжение или гармония */}
                  {m.tension && mc ? (
                    <div className="mt-3 space-y-2">
                      <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm leading-relaxed text-amber-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                        <span><span className="font-semibold">Зона напряжения. </span>{mc.tension}</span>
                      </p>
                      <p className="text-xs text-slate-400">Усилитель: {mc.amplifierTrait} — {m.amplifier}/100</p>
                    </div>
                  ) : m.supported && mc ? (
                    <div className="mt-3 space-y-2">
                      <p className="flex items-start gap-2 rounded-lg bg-emerald-50 p-3 text-sm leading-relaxed text-emerald-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                        <span><span className="font-semibold">В гармонии. </span>{mc.supported}</span>
                      </p>
                      <p className="text-xs text-slate-400">Усилитель: {mc.amplifierTrait} — {m.amplifier}/100</p>
                    </div>
                  ) : mc ? (
                    <p className="mt-3 text-xs text-slate-400">Усилитель: {mc.amplifierTrait} — {m.amplifier}/100</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* ════ ФУТЕР ════ */}
      <div className="p-8">
        <p className="text-center text-xs text-slate-400">
          Синтез рассчитан автоматически на основе 4 профильных тестов · обновляется при пересдаче любого из них
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ num, title }: { num: string; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-heading text-2xl font-bold text-slate-200">{num}</span>
      <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>
    </div>
  );
}

function ScoreCircle({ score, color }: { score: number; color: string }) {
  const size = 56;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - score / 100);

  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size /  2} r={radius} fill="none"
        stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="56%" textAnchor="middle" className="fill-white text-sm font-bold">
        {Math.round(score)}
      </text>
    </svg>
  );
}
